import { Controller } from "@hotwired/stimulus"

// The Fahrzeug-Einsatz rows of the timesheet form: "heute 6–16 Uhr gearbeitet und dabei
// diesen Bagger gehabt".
//
// Three jobs, all on the same rows:
//
// 1. Keeps the vehicle selects in step with the employee selection. Which vehicles an
//    employee may book follows the fixed assignment in the Stammdaten (Team / Mitarbeiter /
//    Poolfahrzeug), so picking different people for a proxy submission changes the list. The
//    server resolves it through Fleets::BookableFleets — the same seam that renders the initial
//    list and enforces the save — and this controller only replaces the options it sends back.
//    Never filter here: a second rule in JS is how the picker and the enforcement drift apart.
//
// 2. Mirrors the entry's own von/bis into every row whose times the user has not touched
//    (data-times-untouched), so the default is "same hours as me" and shortening a row is one
//    edit. A row stops mirroring the moment its own time field changes by hand.
//
// 3. Prefills the vehicle the server resolved (data-primary-fleet-id, refreshed together with
//    the options — Fleets::UsagePrefill decides own/team Primärfahrzeug or the Haupt-Team's)
//    into rows whose vehicle is still blank, swaps it when the employee changes as long as the
//    user has not picked a vehicle themselves (data-vehicle-auto), shows the server's note on
//    why (and its warning for a mixed Kolonne) while a row still carries that vehicle, and
//    shows the driver select only when the submission covers several employees.
//    Never decide the prefill here: the rule lives server-side, like the vehicle list.
//
// The <template> element that nested_form_controller clones for new rows sits in a
// DocumentFragment, which is outside Stimulus' target scope — its selects are found by
// query instead, or a row added after a refresh would carry the stale list.
export default class extends Controller {
  static targets = [
    "employeeSelect",
    "usageSelect",
    "usageRow",
    "usageContainer",
    "driverField",
    "rowTemplate",
    "driverSelect",
    "prefillHint",
    "prefillWarning",
  ]
  // driversOnly: the admin management form deliberately keeps showing every vehicle
  // ("Admins sehen alle"), so the server's answer is used for the Primärfahrzeug only; the
  // driver selects come from the employee picker's own options.
  static values = { url: String, templateId: Number, driversOnly: Boolean }

  connect() {
    // Last request key, so a repeated change event (SlimSelect re-renders on every
    // interaction) costs nothing.
    this.key = null
    // Set while this controller writes into a row's time fields, so the echoing change event
    // is not mistaken for the user touching the row.
    this.mirroring = false
    this.onChange = (event) => this.formChanged(event)
    this.element.addEventListener("change", this.onChange)
    this.mirrorTimes()
  }

  disconnect() {
    this.element.removeEventListener("change", this.onChange)
  }

  // --- rows -------------------------------------------------------------------

  // Fires for the rows rendered with the form and for every row nested_form_controller adds.
  usageRowTargetConnected(row) {
    this.prefillVehicle(row)
    this.mirrorRow(row)
    this.updatePrefillNotes()
  }

  usageRowTargetDisconnected() {
    this.updatePrefillNotes()
  }

  formChanged(event) {
    if (this.mirroring) return

    const name = event.target?.name || ""
    const row = event.target?.closest?.("[data-fleet-usage-options-target~='usageRow']")
    if (row) {
      // The user edited a row's own time or vehicle — from now on it is theirs.
      if (/\[time_(from|to)\]$/.test(name)) row.dataset.timesUntouched = "false"
      if (/\[fleet_id\]$/.test(name)) {
        row.dataset.vehicleAuto = "false"
        this.updatePrefillNotes()
      }
      return
    }
    if (/\[time_(from|to)\]$/.test(name)) this.mirrorTimes()
  }

  mirrorTimes() {
    this.usageRowTargets.forEach((row) => this.mirrorRow(row))
  }

  mirrorRow(row) {
    if (row.dataset.timesUntouched !== "true") return

    const from = this.entryTime("time_from")
    const to = this.entryTime("time_to")
    this.mirroring = true
    try {
      this.writeTime(row, "time_from", from)
      this.writeTime(row, "time_to", to)
    } finally {
      this.mirroring = false
    }
  }

  // The entry's own value under the real form field name: the time picker's hidden companion
  // input, or the plain hidden field of the plus/minus stepper — both carry the name.
  entryTime(field) {
    const input = this.element.querySelector(`input[name="configurable_time_sheet[${field}]"]`)
    return input ? input.value : ""
  }

  // Writing the hidden companion and dispatching change is the time picker's own contract
  // for outside writes (time_picker_controller#valueChangedExternally updates the display).
  // The display is written as well: on first connect the row's picker controllers may not
  // have connected yet, and the dispatched change would then update nothing visible.
  writeTime(row, field, value) {
    const input = row.querySelector(`input[name$="[${field}]"]`)
    if (!input || input.value === value) return

    input.value = value
    const display = input
      .closest('[data-controller~="time-picker"]')
      ?.querySelector('[data-time-picker-target="display"]')
    if (display) display.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Fills a blank vehicle with the Primärfahrzeug; on an employee change also replaces (or
  // clears) a vehicle the system chose earlier. A vehicle the user picked is never touched.
  prefillVehicle(row) {
    const select = row.querySelector("select[data-fleet-usage-options-target~='usageSelect']")
    if (!select) return
    if (select.value && row.dataset.vehicleAuto !== "true") return

    const primary = this.primaryFleetId()
    const offered = Array.from(select.options).some((option) => option.value === primary)
    const next = primary && offered ? primary : ""
    if (select.value === next) return

    // Our own write (and the change event SlimSelect fires on refresh) must not count as
    // the user picking a vehicle — the row stays the system's until they do.
    this.mirroring = true
    try {
      select.value = next
      row.dataset.vehicleAuto = "true"
      this.refreshSlimSelect(select)
    } finally {
      this.mirroring = false
    }
  }

  primaryFleetId() {
    return this.hasUsageContainerTarget
      ? this.usageContainerTarget.dataset.primaryFleetId || ""
      : ""
  }

  // --- options ------------------------------------------------------------------

  refresh() {
    const employeeIds = this.selectedEmployeeIds()
    const key = employeeIds.join(",")
    if (this.key === key) return
    this.key = key

    this.applyDrivers(this.selectedEmployees())
    if (this.hasUrlValue) this.load(employeeIds, key)
  }

  async load(employeeIds, key) {
    const params = new URLSearchParams()
    if (this.hasTemplateIdValue) params.append("timesheet_template_id", this.templateIdValue)
    employeeIds.forEach((id) => params.append("employee_ids[]", id))

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })
      // A refusal is an expected answer rather than a fault (an employee without the
      // create permission): leave the rendered lists alone instead of emptying them.
      // The key is cleared so the next change event retries rather than being deduped
      // against a selection whose list was never applied.
      if (!response.ok) {
        this.key = null
        return
      }

      const data = await response.json()
      // A slower earlier request must not overwrite a newer selection.
      if (this.key !== key) return

      if (!this.driversOnlyValue) {
        this.applyOptions(this.usageSelectTargets, data.fleet_usage_fleets || [])
        this.applyTemplateOptions("usageSelect", data.fleet_usage_fleets || [])
        // The server answers with the employees it accepted, which is the authoritative
        // driver list — the picker may have offered ids the caller may not submit for.
        if (data.employees) this.applyDrivers(data.employees)
      }
      this.applyPrefill(data.prefill)
    } catch (error) {
      this.key = null
      console.error("Failed to load fleet options:", error)
    }
  }

  // The server's answer is the whole decision: a vehicle (or none) plus the note and warning
  // that explain it.
  applyPrefill(prefill) {
    if (!this.hasUsageContainerTarget) return

    this.usageContainerTarget.dataset.primaryFleetId = prefill?.fleet_id
      ? String(prefill.fleet_id)
      : ""
    if (this.hasPrefillHintTarget) this.prefillHintTarget.textContent = prefill?.hint || ""
    if (this.hasPrefillWarningTarget) this.prefillWarningTarget.textContent = prefill?.warning || ""
    this.usageRowTargets.forEach((row) => this.prefillVehicle(row))
    this.updatePrefillNotes()
  }

  // The note belongs to the prefilled vehicle: it shows while a row still carries it on the
  // system's behalf and disappears once the user picks (or clears) the vehicle themselves.
  updatePrefillNotes() {
    const primary = this.primaryFleetId()
    const shown =
      primary !== "" &&
      this.usageRowTargets.some((row) => {
        const select = row.querySelector("select[data-fleet-usage-options-target~='usageSelect']")
        return row.dataset.vehicleAuto === "true" && select?.value === primary
      })
    if (this.hasPrefillHintTarget) {
      this.prefillHintTarget.hidden = !shown || this.prefillHintTarget.textContent === ""
    }
    if (this.hasPrefillWarningTarget) {
      this.prefillWarningTarget.hidden = !shown || this.prefillWarningTarget.textContent === ""
    }
  }

  applyOptions(selects, fleets) {
    selects.forEach((select) => this.replaceOptions(select, fleets))
  }

  // A driver must be one of the recorded employees; with exactly one there is nothing to
  // choose, so it is preselected and the field stays hidden. A driver who has been
  // deselected is cleared rather than silently submitted — the server rejects it either way.
  applyDrivers(employees) {
    const options = employees.map((employee) => ({ id: employee.id, label: employee.label }))
    this.driverSelectTargets.forEach((select) => this.replaceOptions(select, options))
    this.applyTemplateOptions("driverSelect", options)

    const single = options.length <= 1
    this.driverFieldTargets.forEach((field) => (field.hidden = single))
    this.rowTemplateTargets.forEach((template) => {
      template.content
        .querySelectorAll(`[data-${this.identifier}-target~="driverField"]`)
        .forEach((field) => (field.hidden = single))
    })
  }

  // The employee picker's own selected options, used by the drivers-only mode where no
  // request is made: their text is the display name the server would return anyway.
  selectedEmployees() {
    if (!this.hasEmployeeSelectTarget) return []

    return Array.from(this.employeeSelectTarget.selectedOptions)
      .filter((option) => option.value)
      .map((option) => ({ id: option.value, label: option.textContent.trim() }))
  }

  // The clone source lives inside a <template>, so its selects are not Stimulus targets
  // and carry no SlimSelect instance — plain option surgery is all that is needed there.
  applyTemplateOptions(targetName, fleets) {
    if (!this.hasRowTemplateTarget) return

    this.rowTemplateTargets.forEach((template) => {
      const selector = `select[data-${this.identifier}-target~="${targetName}"]`
      template.content
        .querySelectorAll(selector)
        .forEach((select) => this.replaceOptions(select, fleets, { slim: false }))
    })
  }

  // Keeps the blank/prompt option and the current value when it survives the new list;
  // a value that no longer exists is cleared rather than silently submitted.
  replaceOptions(select, fleets, { slim = true } = {}) {
    const previous = select.value
    const blank = Array.from(select.options).find(
      (option) => option.value === "" && option.dataset.placeholder !== "true",
    )

    select.innerHTML = ""
    if (blank) select.append(blank)
    fleets.forEach((fleet) => select.append(new Option(fleet.label, String(fleet.id))))

    const stillThere = fleets.some((fleet) => String(fleet.id) === previous)
    if (stillThere) {
      select.value = previous
    } else if (fleets.length === 1 && select.dataset.autoselectSingle === "true") {
      select.value = String(fleets[0].id)
    } else {
      select.value = blank ? "" : (select.options[0]?.value ?? "")
    }

    if (slim) this.refreshSlimSelect(select)
  }

  // SlimSelect snapshots its data; after mutating the underlying options ask the wrapping
  // select controller to rebuild (same pattern as customer_site_filter).
  refreshSlimSelect(select) {
    this.application.getControllerForElementAndIdentifier(select, "select")?.refresh()
  }

  selectedEmployeeIds() {
    if (!this.hasEmployeeSelectTarget) return []

    const select = this.employeeSelectTarget
    if (select.multiple) {
      return Array.from(select.selectedOptions)
        .map((option) => option.value)
        .filter(Boolean)
    }
    return select.value ? [select.value] : []
  }
}
